import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaSuitcase, FaPlane, FaBed } from 'react-icons/fa';
import { ROUTE_KEYS, SESSION_STORAGE_KEYS } from 'legacyfrontend/constants';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import {
  selectTemplateData,
  selectIsPackageSearcherEnabled,
  selectIsFlightSearcherEnabled,
  selectHasActiveSearchers,
} from 'legacyfrontend/redux/selectors/application-selectors';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';

const FEATURES_CONFIG = [
  {
    id: 'packages',
    label: 'Paquetes',
    icon: FaSuitcase,
    route: ROUTE_KEYS.BUSCAR_X_PAQUETES,
    enabled: true,
    active: true,
  },
  {
    id: 'flights',
    label: 'Vuelos',
    icon: FaPlane,
    route: ROUTE_KEYS.BUSCAR_X_VUELOS,
    enabled: true,
    active: true,
  },
  {
    id: 'hotels',
    label: 'Hoteles',
    icon: FaBed,
    route: '/hoteles',
    enabled: false,
    active: false,
  },
];

const FeatureButton = ({ feature, isActive, onClick }) => {
  const IconComponent = feature.icon;

  const { background } = useColors();

  if (!feature.active) {
    return <></>;
  }

  return (
    <button
      onClick={onClick}
      className={`
        w-full
        min-w-[130px]
        max-w-[140px]
        flex flex-col items-center justify-center
        px-3 transition-all duration-200 h-12 
        ${isActive ? 'bg-white' : 'bg-white/70'}
        lg:backdrop-blur-3xl lg:rounded-lg lg:drop-shadow-lg 
        ${isActive ? 'text-white' : 'text-gray-600 hover:text-gray-800'}
        hover:bg-white-50
        active:bg-gray-100/50 lg:active:bg-white/40
        ${!feature.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        hover:transform hover:-translate-y-1
        active:transform active:scale-95 active:translate-y-0
      `}
      disabled={!feature.enabled}
    >
      <div className='flex flex-row items-center justify-center'>
        <div
          className={`
            w-8 h-8 rounded-full flex items-center justify-center mr-3
            transition-all duration-200
          `}
          style={{
            border: isActive ? `2px solid ${background}` : '2px solid transparent',
          }}
        >
          <IconComponent
            size={20}
            className='text-gray-600'
            style={{
              color: isActive ? background : undefined,
            }}
          />
        </div>

        <span
          className={`text-sm font-medium transition-colors duration-200 text-gray-${isActive ? '800' : '600'}`}
        >
          {feature.label}
        </span>
      </div>
    </button>
  );
};

const FeatureSelector = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeFeature, setActiveFeature] = useState(null);

  const templateData = useSelector(selectTemplateData);
  const { resetOnSearchCount } = useGlobal();

  const isPackageSearcherEnabled = useSelector(selectIsPackageSearcherEnabled);
  const isFlightSearcherEnabled = useSelector(selectIsFlightSearcherEnabled);
  const hasActiveSearchers = useSelector(selectHasActiveSearchers);

  const shouldRender = useMemo(() => {
    if (!hasActiveSearchers) return false;

    const currentPath = location.pathname;
    const isHomePage = currentPath === '/' || currentPath === '';
    const isPackagePage = currentPath.includes(ROUTE_KEYS.BUSCAR_X_PAQUETES);
    const isFlightPage = currentPath.includes(ROUTE_KEYS.BUSCAR_X_VUELOS);

    return isHomePage || isPackagePage || isFlightPage;
  }, [hasActiveSearchers, location.pathname]);

  const enabledFeatures = useMemo(() => {
    return FEATURES_CONFIG.map((feature) => ({
      ...feature,
      active:
        (isPackageSearcherEnabled && feature.id === 'packages') ||
        (isFlightSearcherEnabled && feature.id === 'flights'),
      enabled:
        feature.enabled ||
        (feature.id === 'packages' && templateData?.headerPaquete?.[0]?.esFooter) ||
        (feature.id === 'flights' && templateData?.headerVuelos?.[0]?.esFooter) ||
        feature.enabled,
    }));
  }, [templateData, isPackageSearcherEnabled, isFlightSearcherEnabled]);

  const activeFeatures = useMemo(() => {
    return enabledFeatures.filter((feature) => feature.active);
  }, [enabledFeatures]);

  const clearAllState = useCallback(() => {
    resetOnSearchCount();

    if (isClient) {
      Object.values(SESSION_STORAGE_KEYS).forEach((key) => {
        sessionStorage.removeItem(key);
      });

      sessionStorage.removeItem('flightInfo');
      sessionStorage.removeItem('packageAdvice');

      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            if (cacheName.includes('search') || cacheName.includes('api')) {
              caches.delete(cacheName);
            }
          });
        });
      }
    }
  }, [resetOnSearchCount]);

  const handleFeatureClick = (feature) => {
    if (!feature.enabled) return;

    if (activeFeature && activeFeature !== feature.id) {
      clearAllState();
    }
    setActiveFeature(feature.id);

    if (feature.route) {
      history.push(feature.route);
    }
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const activeFeature = activeFeatures.find(
      (feature) => feature.enabled && currentPath.includes(feature.route)
    );

    if (activeFeature) {
      setActiveFeature(activeFeature.id);
    } else {
      setActiveFeature(null);
    }
  }, [location.pathname, activeFeatures]);

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <div className='flex w-full gap-2 flex-row justify-center'>
        {activeFeatures.map((feature) => (
          <div key={feature.id} className='flex justify-center'>
            <FeatureButton
              feature={feature}
              isActive={activeFeature === feature.id}
              onClick={() => handleFeatureClick(feature)}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default FeatureSelector;
